import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import FilterBar from "./components/FilterBar";
import { channels, movies, regions, years, sorts } from "@/mocks/movies";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 8;

export default function Category() {
  const { type = "" } = useParams();
  const channel = channels.find((c) => c.type === type);

  const [genre, setGenre] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState("hot");
  const [visible, setVisible] = useState(PAGE_SIZE);

  useDocumentMeta(
    `${type}频道 - 芒果tv`,
    `${type}频道汇集芒果tv 精选${type}内容，支持按类型、地区、年份与热度筛选，${channel?.desc ?? "内容丰富，持续更新。"}`
  );

  const baseList = useMemo(() => movies.filter((m) => m.channel === type), [type]);

  const genreOptions = useMemo(() => {
    const set = new Set<string>();
    baseList.forEach((m) => m.genres.forEach((g) => set.add(g)));
    return ["全部", ...Array.from(set)];
  }, [baseList]);

  const filtered = useMemo(() => {
    const list = baseList.filter(
      (m) =>
        (genre === "全部" || m.genres.includes(genre)) &&
        (region === "全部" || m.region === region) &&
        (year === "全部" || String(m.year) === year)
    );
    const sorted = [...list];
    if (sort === "hot") sorted.sort((a, b) => b.popularity - a.popularity);
    if (sort === "update") sorted.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    if (sort === "score") sorted.sort((a, b) => b.score - a.score);
    return sorted;
  }, [baseList, genre, region, year, sort]);

  const shown = filtered.slice(0, visible);

  const resetAll = () => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setSort("hot");
    setVisible(PAGE_SIZE);
  };

  const changeFilter = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setVisible(PAGE_SIZE);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: `${type}频道` }]} />

      <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-6">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
            <i className={`${channel?.icon ?? "ri-tv-2-line"} text-2xl`} />
          </span>
          <div>
            <h1 className="font-heading text-2xl font-extrabold text-foreground-950">
              {type}频道
            </h1>
            <p className="mt-1 text-sm text-foreground-500">
              {channel?.desc ?? "汇集本频道精选内容，持续更新。"}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-5">
        <FilterBar
          genres={genreOptions}
          regions={regions}
          years={years}
          sorts={sorts}
          genre={genre}
          region={region}
          year={year}
          sort={sort}
          onGenre={changeFilter(setGenre)}
          onRegion={changeFilter(setRegion)}
          onYear={changeFilter(setYear)}
          onSort={changeFilter(setSort)}
        />
      </div>

      <p className="mt-4 text-sm text-foreground-500">
        共找到 <strong className="text-primary-600">{filtered.length}</strong> 部{type}作品
      </p>

      {shown.length > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {shown.map((m) => (
              <MovieCard key={m.id} movie={m} />
            ))}
          </div>
          {visible < filtered.length && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                className="rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-600 cursor-pointer whitespace-nowrap"
              >
                加载更多
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-inbox-2-line text-3xl" />
          </span>
          <p className="mt-4 font-heading text-base font-semibold text-foreground-800">
            没有找到符合条件的内容
          </p>
          <p className="mt-1 text-sm text-foreground-500">试试调整筛选条件，或重置后重新浏览。</p>
          <button
            type="button"
            onClick={resetAll}
            className="mt-5 rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            重置筛选
          </button>
        </div>
      )}
    </div>
  );
}